import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type KeyboardEvent,
} from 'react';

export interface MenuItemProps {
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}

export interface MenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}

/**
 * Lets an item close the menu it lives in. Choosing an item is the end of the
 * interaction: an action that stays on the page ("Mark all read") must not
 * leave the menu hanging open (YAR-170 AC7). Items rendered outside a Menu
 * get the no-op default.
 */
const MenuCloseContext = createContext<() => void>(() => {});

export function Menu({ trigger, children, align = 'left' }: MenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open && menuRef.current) {
      const firstItem = menuRef.current.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      );
      firstItem?.focus();
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open || !menuRef.current) return;

      const items = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([aria-disabled="true"])',
        ),
      );
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[next]?.focus();
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prev]?.focus();
          break;
        }
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        case 'Home': {
          e.preventDefault();
          items[0]?.focus();
          break;
        }
        case 'End': {
          e.preventDefault();
          items[items.length - 1]?.focus();
          break;
        }
      }
    },
    [open],
  );

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
    >
      <div onClick={() => setOpen((prev) => !prev)}>{trigger}</div>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          onKeyDown={handleKeyDown}
          className={[
            'absolute top-full mt-1 z-[50]',
            'min-w-[180px] rounded-md border border-border bg-card shadow-lg py-1',
            align === 'right' ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          <MenuCloseContext.Provider value={close}>
            {children}
          </MenuCloseContext.Provider>
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  onClick,
  children,
  icon,
  destructive = false,
  disabled = false,
}: MenuItemProps) {
  const closeMenu = useContext(MenuCloseContext);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      aria-disabled={disabled}
      onClick={() => {
        if (disabled) return;
        closeMenu();
        onClick?.();
      }}
      className={[
        'w-full text-left px-3 py-2 text-sm cursor-pointer',
        'flex items-center gap-2',
        'transition-colors duration-150',
        'focus:outline-none focus-visible:bg-muted',
        disabled
          ? 'text-disabled-text cursor-not-allowed'
          : destructive
            ? 'text-danger hover:bg-danger-light'
            : 'text-foreground hover:bg-muted',
      ].join(' ')}
    >
      {icon && (
        <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

export function MenuDivider() {
  return <div className="my-1 border-t border-border" />;
}
